import React from 'react';
import { useResponsesMonitor } from '../hooks/useResponsesMonitor';
import ExpandableText from './ExpandableText';
import { RefreshCw, Clock, Cpu, DollarSign } from 'lucide-react';
import type { PromptResponse } from '../types/responses';

const ResponsesMonitor: React.FC = () => {
  const {
    responses,
    loading,
    error,
    expandedItems,
    actions: { refreshResponses, toggleExpanded, clearError }
  } = useResponsesMonitor();

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const formatCost = (cost: number): string => {
    return `$${cost.toFixed(5)}`;
  };

  const formatTime = (time: number): string => {
    return `${time}ms`;
  };

  const renderResponseCard = (response: PromptResponse) => {
    const promptTitle = response.prompt_name || `Prompt ID: ${response.prompt_id}`;
    const promptContent = response.prompt_text || 'No prompt text available';

    return (
      <div key={response.id} className="response-card">
        <div className="response-header">
          <div className="response-meta">
            <div className="response-id">ID: {response.id}</div>
            <div className="response-date">{formatDate(response.created_at)}</div>
          </div>
          <div className="response-stats">
            <div className="stat-item">
              <Cpu size={14} />
              <span>{response.input_tokens + response.output_tokens} tokens</span>
            </div>
            <div className="stat-item">
              <DollarSign size={14} />
              <span>{formatCost(response.cost)}</span>
            </div>
            <div className="stat-item">
              <Clock size={14} />
              <span>{formatTime(response.time)}</span>
            </div>
          </div>
        </div>

        <div className="response-content">
          <ExpandableText
            title={promptTitle}
            content={promptContent}
            isExpanded={expandedItems.has(`prompt-${response.id}`)}
            onToggle={() => toggleExpanded(`prompt-${response.id}`)}
            type="prompt"
            maxPreviewLength={80}
          />

          <ExpandableText
            title="Messages"
            content={response.messages}
            isExpanded={expandedItems.has(`messages-${response.id}`)}
            onToggle={() => toggleExpanded(`messages-${response.id}`)}
            type="messages"
            maxPreviewLength={80}
          />

          <ExpandableText
            title="Response"
            content={response.response}
            isExpanded={expandedItems.has(`response-${response.id}`)}
            onToggle={() => toggleExpanded(`response-${response.id}`)}
            type="response"
            maxPreviewLength={120}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="responses-monitor">
      <div className="monitor-header">
        <h3>Monitor de Respuestas</h3>
        <button 
          className="refresh-button"
          onClick={refreshResponses}
          disabled={loading}
        >
          <RefreshCw size={16} className={loading ? 'spinning' : ''} />
          Actualizar
        </button>
      </div>

      {error && (
        <div className="error-banner">
          <span>{error}</span>
          <button onClick={clearError}>×</button>
        </div>
      )}

      <div className="responses-info">
        <p>Últimas 10 respuestas del sistema de IA</p>
      </div>

      <div className="responses-list">
        {loading && responses.length === 0 ? (
          <div className="loading-state">
            <RefreshCw size={24} className="spinning" />
            <span>Cargando respuestas...</span>
          </div>
        ) : responses.length === 0 ? (
          <div className="empty-state">
            <p>No hay respuestas disponibles</p>
          </div>
        ) : (
          responses.map(renderResponseCard)
        )}
      </div>

      <style>{`
        .responses-monitor {
          height: 100%;
          display: flex;
          flex-direction: column;
        }

        .monitor-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
          flex-shrink: 0;
        }

        .monitor-header h3 {
          margin: 0;
          color: #333;
          font-size: 20px;
        }

        .refresh-button {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 16px;
          background: #4CAF50;
          color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-size: 14px;
          transition: all 0.2s;
        }

        .refresh-button:hover:not(:disabled) {
          background: #45a049;
        }

        .refresh-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .spinning {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .error-banner {
          background: #ffebee;
          color: #c62828;
          padding: 12px;
          border-radius: 4px;
          margin-bottom: 20px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-shrink: 0;
        }

        .error-banner button {
          background: none;
          border: none;
          color: #c62828;
          font-size: 18px;
          cursor: pointer;
          padding: 0;
          width: 20px;
          height: 20px;
        }

        .responses-info {
          margin-bottom: 15px;
          flex-shrink: 0;
        }

        .responses-info p {
          margin: 0;
          color: #666;
          font-size: 14px;
        }

        .responses-list {
          flex: 1;
          overflow-y: auto;
          min-height: 0;
        }

        .response-card {
          background: white;
          border: 1px solid #e0e0e0;
          border-radius: 8px;
          margin-bottom: 16px;
          overflow: hidden;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .response-header {
          background: #f8f9fa;
          padding: 12px 16px;
          border-bottom: 1px solid #e0e0e0;
        }

        .response-meta {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 8px;
        }

        .response-id {
          font-weight: 600;
          color: #333;
          font-size: 14px;
        }

        .response-date {
          color: #666;
          font-size: 13px;
        }

        .response-stats {
          display: flex;
          gap: 16px;
        }

        .stat-item {
          display: flex;
          align-items: center;
          gap: 4px;
          color: #666;
          font-size: 12px;
        }

        .response-content {
          padding: 16px;
        }

        .loading-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 40px;
          color: #666;
          gap: 12px;
        }

        .empty-state {
          text-align: center;
          padding: 40px;
          color: #888;
        }

        .empty-state p {
          margin: 0;
          font-size: 16px;
        }
      `}</style>
    </div>
  );
};

export default ResponsesMonitor;
